import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ChurnRatesResult, type RetentionCurveResult } from '@whinerp/client';
import { EmptyState, ErrorState, PageHeading, Panel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-77 Churn and retention (G-133).
 *
 * The screen's one job is to stop a reader leaving with a single number in
 * their head.
 *
 * Logo churn and revenue retention sit side by side at the same size, and the
 * sentence underneath says which way they point when they disagree - because
 * two percentages on tiles get reconciled by believing whichever tile was
 * bigger.
 *
 * A rate that does not exist is drawn as "no rate", never as 0%. A month that
 * opened with no customers did not retain everybody.
 *
 * Cohorts stop where their history stops. A young cohort padded with zeroes
 * reads as a cliff, and the cliff is just today.
 */

/** A rate, or the reason there isn't one. Never a silent zero. */
function Rate({ value, none }: { value: number | null; none: string }): JSX.Element {
  if (value === null) {
    return (
      <span className="text-body text-ink-meta" data-rate="none">
        {none}
      </span>
    );
  }
  return (
    <span className="text-title text-ink" data-rate={String(value)}>
      {`${(value * 100).toFixed(1)}%`}
    </span>
  );
}

function ChurnScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();

  const window_ = useMemo(() => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0));
    return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
  }, []);

  const rates = useQuery<ChurnRatesResult, ApiError>({
    queryKey: ['churn-rates', window_.from, window_.to],
    queryFn: () => api.churnRates({ query: window_ }),
  });

  const retention = useQuery<RetentionCurveResult, ApiError>({
    queryKey: ['retention-curve'],
    queryFn: () => api.retentionCurve({ query: { months: 12 } }),
  });

  if (rates.isPending) return <Loading />;
  if (rates.isError) {
    return (
      <div data-component="Churn">
        <ErrorState title={t('state.error')} body={errorText(rates.error)} onRetry={() => void rates.refetch()} />
      </div>
    );
  }

  const figures = rates.data.rates;

  return (
    <div data-component="Churn" className="flex w-full flex-col gap-4">
      <PageHeading>{t('churn.title')}</PageHeading>
      <Link to="/revenue/unit-economics" data-open-unit-economics className="self-start text-dense text-bronze underline">{t('economics.title')}</Link>
      <p className="text-body text-ink-meta">{t('churn.help')}</p>

      {/* Same size, side by side. Neither is "the" churn rate. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Panel data-churn-tile="logo">
          <span className="text-meta text-ink-meta">{t('churn.logo')}</span>
          <div>
            <Rate value={figures.logoChurnRate} none={t('churn.no-rate')} />
          </div>
          <span className="text-meta text-ink-meta">
            {t('churn.logo.detail', {
              values: { lost: String(figures.churnedCustomers), opening: String(figures.openingCustomers) },
            })}
          </span>
        </Panel>

        <Panel data-churn-tile="net">
          <span className="text-meta text-ink-meta">{t('churn.net')}</span>
          <div>
            <Rate value={figures.netRetention} none={t('churn.no-rate')} />
          </div>
          <span className="text-meta text-ink-meta">{t('churn.net.detail')}</span>
        </Panel>

        <Panel data-churn-tile="gross">
          <span className="text-meta text-ink-meta">{t('churn.gross')}</span>
          <div>
            <Rate value={figures.grossRetention} none={t('churn.no-rate')} />
          </div>
          <span className="text-meta text-ink-meta">{t('churn.gross.detail')}</span>
        </Panel>
      </div>

      {/* The sentence, because the tiles alone get reconciled wrongly. */}
      <p className="text-body text-ink" data-churn-summary>
        {figures.summary}
      </p>

      {retention.data && retention.data.cohorts.length > 0 ? (
        <Panel data-churn-cohorts>
          <h2 className="text-body text-ink">{t('churn.cohorts')}</h2>
          <p className="text-meta text-ink-meta">{t('churn.cohorts.help')}</p>
          <div className="mt-2 overflow-x-auto" data-scroll-container>
            <table className="w-full text-dense">
              <thead>
                <tr className="text-meta text-ink-meta">
                  <th className="p-2 text-start">{t('churn.column.cohort')}</th>
                  <th className="p-2 text-end">{t('churn.column.size')}</th>
                  <th className="p-2 text-start">{t('churn.column.retained')}</th>
                </tr>
              </thead>
              <tbody>
                {retention.data.cohorts.map((cohort) => (
                  <tr key={cohort.cohort} className="border-t border-line" data-cohort={cohort.cohort}>
                    <td className="p-2 text-ink-600">{cohort.cohort.slice(0, 7)}</td>
                    <td className="p-2 text-end text-ink">{cohort.size}</td>
                    <td className="p-2">
                      <span className="flex flex-wrap gap-1">
                        {/* Only the months this cohort has actually lived
                            through. A padded zero would read as a collapse. */}
                        {cohort.points.map((point) => (
                          <span
                            key={point.month}
                            data-cohort-point={String(point.month)}
                            className="rounded-control bg-sunken px-2 text-meta text-ink-600"
                          >
                            {point.share === null ? '—' : `${Math.round(point.share * 100)}%`}
                          </span>
                        ))}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {retention.data.incomplete.length > 0 ? (
            <p className="mt-2 text-meta text-ink-meta" data-cohorts-incomplete>
              {t('churn.cohorts.incomplete', { values: { count: String(retention.data.incomplete.length) } })}
            </p>
          ) : null}
        </Panel>
      ) : null}
    </div>
  );
}

function Loading(): JSX.Element {
  return (
    <div data-component="Churn">
      <Skeleton className="h-plot w-full" />
    </div>
  );
}

function Empty(): JSX.Element {
  const t = useT();
  return (
    <div data-component="Churn">
      <EmptyState title={t('churn.empty')} body={t('churn.empty.help')} />
    </div>
  );
}

function Failed(): JSX.Element {
  const t = useT();
  return (
    <div data-component="Churn">
      <ErrorState title={t('state.error')} body={t('churn.help')} />
    </div>
  );
}

export const screen = defineScreen({
  id: 'SC-77',
  route: '/revenue/churn',
  surface: 'page',
  title: 'Churn and retention',
  features: ['F-080'],
  entitlement: 'F-080',
  reachedBy: [{ kind: 'screen', from: 'SC-45', how: 'Open churn and retention from the revenue screen' }],
  components: [],
  elements: ['Churn'],
  states: { empty: Empty, loading: Loading, error: Failed },
  component: ChurnScreen,
});

export default screen;
